// src/devices/store.tsx
import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import type { DataScope, DeviceRecord, DeviceState, DeviceStatus, Provider } from "./types";
import { connectors } from "./registry";

const ALL_PROVIDERS: Provider[] = [
  "Fitbit","Oura","Apple Watch","Google Fit","Garmin","WHOOP","Polar","Suunto",
  "Muse","OpenBCI","Emotiv","Meta Quest","Tesla Suit","Lumina Vibe","Lumina One",
];

const VERSION = "v1";
const LS_KEY = `gb.devices.${VERSION}`;

function makeInitial(): DeviceState {
  const recs: Partial<DeviceState> = {};
  ALL_PROVIDERS.forEach((p) => (recs[p] = { provider: p, status: "not_linked" }));
  return recs as DeviceState;
}

type Action =
  | { type: "HYDRATE"; payload: DeviceState }
  | { type: "SET_STATUS"; provider: Provider; status: DeviceStatus; patch?: Partial<DeviceRecord> }
  | { type: "SET_SCOPES"; provider: Provider; scopes: DataScope[] }
  | { type: "UNLINK"; provider: Provider };

function reducer(state: DeviceState, action: Action): DeviceState {
  switch (action.type) {
    case "HYDRATE":
      return action.payload;
    case "SET_STATUS": {
      const prev = state[action.provider];
      return { ...state, [action.provider]: { ...prev, status: action.status, ...action.patch } };
    }
    case "SET_SCOPES": {
      const prev = state[action.provider];
      return { ...state, [action.provider]: { ...prev, scopes: action.scopes } };
    }
    case "UNLINK": {
      const prev = state[action.provider];
      return { ...state, [action.provider]: { provider: action.provider, status: "not_linked" } as DeviceRecord };
    }
    default:
      return state;
  }
}

type StoreCtx = {
  state: DeviceState;
  link(provider: Provider): Promise<void>;
  unlink(provider: Provider): Promise<void>;
  markSync(provider: Provider, patch?: Partial<DeviceRecord>): void;
  setScopes(provider: Provider, scopes: DataScope[]): void;
};

const Ctx = createContext<StoreCtx | null>(null);

export function DeviceStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined as any, makeInitial);

  // hydrate from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DeviceState;
        // basic schema sanity: all providers present
        const merged = { ...makeInitial(), ...parsed };
        dispatch({ type: "HYDRATE", payload: merged });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // persist on change
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const actions = useMemo<StoreCtx>(() => ({
    state,
    async link(provider) {
      dispatch({ type: "SET_STATUS", provider, status: "syncing", patch: { errorMsg: undefined } });
      try {
        const connector = connectors[provider];
        const result = await connector.connect();
        dispatch({ type: "SET_STATUS", provider, status: "connected", patch: result });
      } catch (err: any) {
        dispatch({ type: "SET_STATUS", provider, status: "error", patch: { errorMsg: String(err?.message || err) } });
      }
    },
    async unlink(provider) {
      try { await connectors[provider]?.disconnect?.(); } catch { /* ignore */ }
      dispatch({ type: "UNLINK", provider });
    },
    markSync(provider, patch) {
      dispatch({ type: "SET_STATUS", provider, status: "connected", patch: { lastSync: "now", ...patch } });
    },
    setScopes(provider, scopes) {
      dispatch({ type: "SET_SCOPES", provider, scopes });
    },
  }), [state]);

  return <Ctx.Provider value={actions}>{children}</Ctx.Provider>;
}

export function useDevices() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("DeviceStoreProvider missing");
  const all = Object.values(ctx.state);
  const connected = all.filter(d => d.status === "connected");
  return { ...ctx, all, connected };
}
// compat alias so legacy imports keep working
export { useDevices as useDevice };

